<template>
  <CustomDrawer
    v-model:modelValue="internalDrawerVisible"
    direction="right"
    width="600px"
    title="Compose Email"
  >
    <div v-if="!isLoading" class="flex flex-col gap-2">
      <CustomAlerts
        v-if="senderEmails?.length <= 0"
        type="warning"
        title="Connect Your Email"
        description="Please connect your email account to send & receive emails within SuperAGI"
        source="email_compose"
      >
        <div class="flex-row flex justify-start align-center gap-2">
          <a-button
            class="primary_medium w-fit my-2"
            @click="toConnectEmailVendor('GMAIL')"
          >
            <ThemeImage
              :icon="imageNames.GMAIL_LOGO"
              alt="gmail_logo"
              class="w-4 h-3"
            />
            Connect Gmail
          </a-button>

          <a-button
            class="primary_medium w-fit my-2"
            @click="toConnectEmailVendor('OUTLOOK')"
          >
            <ThemeImage
              :icon="imageNames.OUTLOOK_LOGO"
              alt="outlook_logo"
              class="w-4 h-3"
            />
            Connect Outlook
          </a-button>
        </div>
      </CustomAlerts>

      <div
        class="flex flex-col secondary_color font-normal text-sm mail_reply_container min-h-[500px] overflow-y-scroll custom_scrollbar"
      >
        <div id="from_field" class="form_row p-2 border_bottom">
          <span>From: </span>
          <a-select
            v-if="senderEmails && senderEmails.length > 1"
            class="w-full custom-select"
            v-model:value="emailForm.from"
            :showArrow="true"
            :disabled="isReply || isForward"
          >
            <a-select-option
              v-for="emailAccount in senderEmails"
              :key="emailAccount.id"
              :value="emailAccount.email"
            >
              {{ emailAccount.email }}
            </a-select-option>
          </a-select>
          <a-input
            v-else
            v-model:value="emailForm.from"
            class="mail_reply_fields"
            disabled
          />
        </div>

        <div id="to_field" class="form_row p-2 border_bottom">
          <div class="flex flex-row justify-between items-center w-full">
            <div
              class="flex flex-row items-center gap-2 min-w-0"
              :class="{ 'overflow-hidden': makeMultiInputScrollable }"
            >
              <span class="flex-shrink-0">To: </span>
              <div class="flex-1 min-w-0">
                <MultiSelectInput
                  v-model:modelValue="emailForm.to"
                  valueField="email"
                  class="mail_reply_fields w_full"
                  placeholder="To"
                  :options="listOfContacts"
                  :onInput="handleInput"
                  :formatItem="formatContact"
                  :autofocus="!emailForm?.to?.length"
                  :scrollable="makeMultiInputScrollable"
                  fieldId="to"
                  :draggable="true"
                  @item-dropped="handleRecipientDrop"
                />
              </div>
            </div>
            <div
              class="flex flex-row gap-2 secondary_color text-sm font-medium flex-shrink-0 ml-2"
            >
              <span v-if="!showCC" @click="showCC = true" class="cursor-pointer"
                >CC</span
              >
              <span
                v-if="!showBCC"
                @click="showBCC = true"
                class="cursor-pointer"
                >BCC</span
              >
            </div>
          </div>
        </div>

        <div id="cc_field" class="form_row p-2 border_bottom" v-if="showCC">
          <div class="flex flex-row justify-between items-center w-full">
            <div
              class="flex flex-row items-center gap-2 min-w-0"
              :class="{ 'overflow-hidden': makeMultiInputScrollable }"
            >
              <span class="flex-shrink-0">CC: </span>
              <div class="flex-1 min-w-0">
                <MultiSelectInput
                  v-model:modelValue="emailForm.cc"
                  valueField="email"
                  placeholder="CC"
                  class="mail_reply_fields w_full"
                  :options="listOfContacts"
                  :onInput="handleInput"
                  :formatItem="formatContact"
                  :scrollable="makeMultiInputScrollable"
                  fieldId="cc"
                  :draggable="true"
                  @item-dropped="handleRecipientDrop"
                />
              </div>
            </div>
            <div class="flex justify-end flex-shrink-0 ml-2">
              <ThemeImage
                @click="showCC = false"
                :icon="imageNames.CLOSE_GREY"
                alt="close_icon"
                class="size-4 cursor-pointer"
              />
            </div>
          </div>
        </div>

        <div id="bcc_field" class="form_row p-2 border_bottom" v-if="showBCC">
          <div class="flex flex-row justify-between items-center w-full">
            <div
              class="flex flex-row items-center gap-2 min-w-0"
              :class="{ 'overflow-hidden': makeMultiInputScrollable }"
            >
              <span class="flex-shrink-0">BCC: </span>
              <div class="flex-1 min-w-0">
                <MultiSelectInput
                  v-model:modelValue="emailForm.bcc"
                  valueField="email"
                  placeholder="BCC"
                  class="mail_reply_fields w_full"
                  :options="listOfContacts"
                  :onInput="handleInput"
                  :formatItem="formatContact"
                  :scrollable="makeMultiInputScrollable"
                  fieldId="bcc"
                  :draggable="true"
                  @item-dropped="handleRecipientDrop"
                />
              </div>
            </div>
            <div class="flex justify-end flex-shrink-0 ml-2">
              <ThemeImage
                @click="showBCC = false"
                :icon="imageNames.CLOSE_GREY"
                alt="close_icon"
                class="size-4 cursor-pointer"
              />
            </div>
          </div>
        </div>

        <div id="subject_field" class="form_row p-2 border_bottom">
          <span>Subject: </span>
          <div class="relative w-full">
            <a-input
              v-model:value="emailForm.subject"
              placeholder="Enter Subject"
              class="mail_reply_fields"
              @focus="inputInFocus = 'subject'"
              ref="subjectInputRef"
              :disabled="isReply || isForward"
            />
            <div class="absolute top-[7px] right-[10px] flex items-center">
              <PersonalizationDropdown
                @apply-personalization="applyPersonalization"
                position="start"
                :isSubjectField="true"
                :onHover="true"
              />
            </div>
          </div>
        </div>

        <QuillEditor
          placeholder="Enter Message"
          v-model:modelValue="emailForm.message"
          class="w-full text_color"
          ref="quillEditorRef"
          :download-api="uploadDownloadAPITypes.CONVERSATION_ATTACHMENTS"
          :attachments="attachments"
          @editor-click="inputInFocus = 'quill'"
          @remove-attachment="removeAttachment"
        />
      </div>
    </div>

    <div class="flex flex-row justify-end items-center gap-4">
      <div class="pt-1">
        <AttachmentButton
          source="email_compose"
          :visible="showAttachButton"
          @file-selected="handleFileChange"
        />
      </div>
      <InsertDropdown
        v-model="emailForm.message"
        :step-type="stepType"
        @update:modelValue="handleInsertUpdate"
        @insert-sendspark-video="handleSendsparkInsert"
        class="pt-2"
      />
      <CustomDropdown maxHeight="300px" gap="10px" position="top">
        <template #trigger>
          <div
            class="flex flex-row gap-1 items-center pt-2 cursor-pointer text-sm"
          >
            <ThemeImage
              :icon="imageNames.STACKED_EMAIL_GREY"
              alt="attachment_icon"
              class="size-5"
            />

            Email Template

            <ThemeImage
              :icon="imageNames.BOTTOM_ARROW"
              alt="bottom_arrow_grey"
              class="size-4"
            />
          </div>
        </template>

        <template #default>
          <div class="flex flex-col gap-1 py-3 px-2">
            <span class="text-[11px] font-semibold text_color opacity-60 px-3">
              EMAIL TEMPLATES
            </span>

            <a-tooltip
              v-for="(item, index) in templates"
              :key="item.id + '_' + index"
              placement="top"
              :title="
                shouldShowTooltip(item.id + '_' + index) ? item.name || '' : ''
              "
            >
              <div
                class="dropdown_item flex items-center flex-row gap-2 px-3 py-2 rounded-lg"
                @click="applyTemplate(item.id)"
              >
                <div class="flex flex-col gap-1">
                  <div
                    :ref="(el) => setContainerRef(el, item.id + '_' + index)"
                    class="truncate"
                  >
                    <div
                      :ref="(el) => setTextRef(el, item.id + '_' + index)"
                      class="text-sm truncate text_color"
                    >
                      {{ item.name }}
                    </div>
                  </div>
                  <div class="text-xs secondary_color text-wrap max-w-[12vw]">
                    {{ item.subject }}
                  </div>
                </div>
              </div>
            </a-tooltip>
          </div>
        </template>
      </CustomDropdown>

      <div class="pt-2">
        <PersonalizationDropdown
          @apply-personalization="applyPersonalization"
          position="topend"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex flex-row justify-end">
        <a-button
          @click="sendEmail"
          class="primary_medium"
          :loading="isSending"
          :disabled="!(senderEmails?.length > 0) || isUploading"
          >Send Email
        </a-button>
      </div>
    </template>
  </CustomDrawer>
</template>

<script setup>
import { ref, reactive, watch, nextTick, onMounted, computed } from "vue";
import { message } from "ant-design-vue";
import DashboardService from "@/src/api/DashboardService.js";
import CRMService from "@/src/api/CRMService.js";
import TaskService from "@/src/api/TaskService.js";
import CustomDrawer from "@/src/components/CustomDrawer.vue";
import QuillEditor from "@/src/components/quill_editor/QuillEditor.vue";
import MultiSelectInput from "@/src/components/MultiSelectInput.vue";
import CustomDropdown from "@/src/components/CustomDropdown.vue";
import InsertDropdown from "@/src/components/InsertDropdown.vue";
import PersonalizationDropdown from "@/src/components/PersonalizationDropdown.vue";
import CustomAlerts from "@/src/components/CustomAlerts.vue";
import { connectIntegration, formatContact, showAlreadyExistsMessage } from "@/src/utils/utils.js";
import eventBus from "@/src/utils/eventBus.js";
import { useRoute, useRouter } from "vue-router";
import { handleEmailFormatting } from "@/src/utils/conversation_utils.js";
import { debounce } from "lodash";
import { transformCRMSearchResult } from "@/src/utils/crmFilterUtils.js";
import { uploadDownloadAPITypes } from "@/src/constants/commonConstants.js";
import AttachmentButton from "@/src/components/attachment_components/AttachmentButton.vue";
import { useFileUpload } from "@/src/composables/useFileUpload.js";
import { imageNames } from "@/src/constants/imageConstants";
import { useTruncationTooltip } from "@/src/composables/useTruncationTooltip.js";
import { useRecipientDrop } from "@/src/composables/useRecipientDrop.js";

const props = defineProps({
  visible: {
    type: Boolean,
    required: true,
  },
  recipientTo: {
    type: [Array, Object],
    required: false,
    default: () => [],
  },
  toList: { type: Array, default: () => [] },
  source: {
    type: String,
    required: true,
  },
  prefilledEmailData: {
    type: Object,
    default: null,
  },
  isReply: {
    type: Boolean,
    default: false,
  },
  isForward: {
    type: Boolean,
    default: false,
  },
  autoFocus: {
    type: Boolean,
    default: false,
  },
  taskId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(["update:visible", "email-sent"]);

const { setTextRef, setContainerRef, shouldShowTooltip } =
  useTruncationTooltip();

const route = useRoute();
const router = useRouter();

const {
  attachments,
  uploadFiles,
  removeAttachment,
  initializeAttachments,
  resetAttachments,
  isUploading,
} = useFileUpload({
  api: uploadDownloadAPITypes.CONVERSATION_ATTACHMENTS,
});

const emailForm = reactive({
  to: [],
  from: "",
  subject: "",
  message: "",
  cc: [],
  bcc: [],
  thread_id: null,
  message_id: null,
  is_reply_all: false,
  is_forward: false,
});

const minimized = ref(false);
const templates = ref([]);
const insertedCampaignIds = ref(new Set());
const selectedTemplateId = ref(null);
const internalDrawerVisible = ref(props.visible);
const showCC = ref(false);
const showBCC = ref(false);
const senderEmails = ref(null);
const quillEditorRef = ref(null);
const subjectInputRef = ref(null);
const showTemplateDropdown = ref(false);
const isLoading = ref(false);
const isSending = ref(false);
const integrations = ref(null);
const listOfContacts = ref([]);
const inputInFocus = ref("quill");
const showAttachButton = ref(false);
const signatures = ref([]);
const emailIntegrationOptions = ref(null);
const stepType = ref("EMAIL");
const normalizeRecipientTo = (recipientTo) => {
  if (!recipientTo) return [];

  if (Array.isArray(recipientTo)) {
    return recipientTo;
  }

  if (typeof recipientTo === "object" && recipientTo.email) {
    return [recipientTo];
  }

  return [];
};

const extractEmails = (recipients) => {
  return recipients.map((recipient) => recipient?.email).filter(Boolean);
};

const makeMultiInputScrollable = computed(() => {
  return (
    emailForm?.to?.length + emailForm?.cc?.length + emailForm?.bcc?.length > 6
  );
});

const handleSendsparkInsert = (videoData) => {
  if (!quillEditorRef.value) return;
  quillEditorRef.value.insertVideoEmbed(videoData);
};

const handleInsertUpdate = (value) => {
  if (!quillEditorRef.value) return;

  const quill = quillEditorRef.value.getQuill();
  if (!quill) return;

  const range = quill.getSelection();
  const position = range
    ? range.index
    : (quill.selection.savedRange?.index ?? quill.getLength());

  if (value && typeof value === "object") {
    quill.insertText(position, value.text);
    quill.formatText(position, value.text.length, "link", value.url);
    quill.setSelection(position + value.text.length);
  } else if (typeof value === "string") {
    const newLinePosition = quill.getLength();
    quill.insertText(newLinePosition, `\n${value}`);
    quill.setSelection(newLinePosition + value.length + 1);
  }
  emailForm.message = quill.root.innerHTML;
};

const initializeEmailForm = () => {
  if (props.prefilledEmailData) {
    emailForm.subject = props.prefilledEmailData.subject || "";
    emailForm.message = props.prefilledEmailData.content || "";

    if (props.prefilledEmailData.to && props.prefilledEmailData.to.length > 0) {
      emailForm.to = props.prefilledEmailData.to;
    } else {
      const normalizedRecipients = normalizeRecipientTo(props.recipientTo);
      if (normalizedRecipients.length > 0) {
        emailForm.to = extractEmails(normalizedRecipients);
      }
    }

    emailForm.from = props.prefilledEmailData.from || "";
    emailForm.cc = props.prefilledEmailData.cc || [];
    emailForm.bcc = props.prefilledEmailData.bcc || [];

    if (props?.prefilledEmailData?.attachments) {
      initializeAttachments(props.prefilledEmailData.attachments);
    }

    emailForm.thread_id = props.prefilledEmailData.thread_id || null;
    emailForm.message_id = props.prefilledEmailData.message_id || null;
    emailForm.is_reply_all = props.prefilledEmailData.is_reply_all || false;
    emailForm.is_forward = props.isForward || false;

    if (props.prefilledEmailData.cc && props.prefilledEmailData.cc.length > 0) {
      showCC.value = true;
    }
    if (
      props.prefilledEmailData.bcc &&
      props.prefilledEmailData.bcc.length > 0
    ) {
      showBCC.value = true;
    }
  } else {
    const normalizedRecipients = normalizeRecipientTo(props.recipientTo);
    if (normalizedRecipients.length > 0) {
      emailForm.to = extractEmails(normalizedRecipients);
    } else {
      emailForm.to = props.toList?.filter(Boolean) || [];
    }
  }
};

const handleFileChange = async (files = []) => {
  if (files?.length === 0) return;
  await uploadFiles(files);
};

const updateRecipientTo = () => {
  if (!props.prefilledEmailData) {
    const normalizedRecipients = normalizeRecipientTo(props.recipientTo);
    if (normalizedRecipients.length > 0) {
      emailForm.to = extractEmails(normalizedRecipients);
    } else {
      emailForm.to = [];
    }
  }
};

const resetFormState = () => {
  emailForm.cc = [];
  emailForm.bcc = [];
  emailForm.to = [];
  emailForm.subject = "";
  emailForm.message = "";
  selectedTemplateId.value = null;
  minimized.value = false;
  showCC.value = false;
  showBCC.value = false;

  emailForm.thread_id = null;
  emailForm.message_id = null;
  emailForm.is_reply_all = false;
  emailForm.is_forward = false;
  insertedCampaignIds.value.clear();

  resetAttachments();

  const normalizedRecipients = normalizeRecipientTo(props.recipientTo);
  if (normalizedRecipients.length > 0) {
    emailForm.to = extractEmails(normalizedRecipients);
  }
};

const fetchTemplates = async () => {
  try {
    const response = await DashboardService.getEmailTemplates();
    if (response.success) {
      templates.value = response.templates;
    } else {
      message.error("Failed to fetch email templates");
    }
  } catch (error) {
    console.error("Error fetching email templates:", error);
    message.error("An error occurred while fetching email templates");
  }
};

const applyTemplate = (templateId) => {
  if (templateId === null) {
    emailForm.subject = "";
    emailForm.message = "";
    return;
  }

  selectEmailTemplateForContact(templateId);
  showTemplateDropdown.value = false;
};

const selectEmailTemplateForContact = async (templateId) => {
  const entityInfo = extractEntityFromUrl(route.path) || {};
  let output = await DashboardService.selectEmailTemplateForContact(
    templateId,
    emailForm.to,
    entityInfo.entity_id,
    entityInfo.entity_type,
  );

  const selectedTemplate = output.template;
  if (selectedTemplate) {
    emailForm.subject = selectedTemplate.subject;
    emailForm.message = selectedTemplate.body;
  }
  showTemplateDropdown.value = false;
};

const applyPersonalization = (tag) => {
  if (inputInFocus.value === "subject") {
    const input = subjectInputRef.value.input;
    const cursorPosition = input.$el.selectionStart;
    const currentValue = emailForm.subject;
    emailForm.subject =
      currentValue.slice(0, cursorPosition) +
      tag +
      currentValue.slice(cursorPosition);
    nextTick(() => {
      input.setSelectionRange(
        cursorPosition + tag.length,
        cursorPosition + tag.length,
      );
      input.focus();
    });
    return;
  }

  const quill = quillEditorRef.value.getQuill();
  if (!quill) return;
  quill.focus();
  const range = quill.getSelection();
  const insertPosition = range ? range.index : quill.getLength() - 1;
  quill.insertText(insertPosition, tag);
  quill.setSelection(insertPosition + tag.length, 0);

  emailForm.message = quill.root.innerHTML;
};

const fetchSignature = async () => {
  try {
    const response = await DashboardService.fetchMailboxSignatures();
    if (response.success) {
      signatures.value = response.signatures;
    }
  } catch (error) {
    console.error("Error fetching signature:", error);
    message.error("Failed to fetch signature");
  }
};

const addSignature = () => {
  const fromEmail = emailForm.from;
  const signature = signatures.value.find((e) => e.email === emailForm.from);
  if (signature.signature_type == "text") {
    emailForm.message += `${signature.signature}`;
    quillEditorRef.value.setContents(emailForm.message);
  } else if (signature.signature_type == "html") {
    emailForm.message += `{email.signature}`;
    quillEditorRef.value.setContents(emailForm.message);
  } else {
    message.info(
      "No signature available. Please set up your signature in settings.",
    );
  }
};

const sendEmail = async () => {
  isSending.value = true;
  try {
    const formatEmails = (emails) => emails.map((email) => ({ email }));

    const payload = {
      from: emailForm.from,
      to: formatEmails(emailForm.to),
      subject: emailForm.subject,
      message: emailForm.message,
      cc: formatEmails(emailForm.cc),
      bcc: formatEmails(emailForm.bcc),
      attachment_file_ids: attachments?.value?.map((a) => a.id) || [],
    };

    if (props.taskId) {
      payload.task_id = props.taskId;
    }

    if (props.prefilledEmailData?.sequence_execution_step_id) {
      payload.sequence_id = props.prefilledEmailData.sequence_id;
      payload.sequence_execution_id = props.prefilledEmailData.sequence_execution_id;
      payload.sequence_execution_step_id = props.prefilledEmailData.sequence_execution_step_id;
      payload.entity_id = props.prefilledEmailData.entity_id;
      payload.entity_type = props.prefilledEmailData.entity_type;
      payload.account_id = props.prefilledEmailData.email_account_id;
      payload.reply_to_previous_thread = props.prefilledEmailData.reply_to_previous_thread || false;
    }

    if (props.isReply || props.isForward) {
      Object.assign(payload, {
        message:
          emailForm.message +
          handleEmailFormatting(
            emailForm,
            props.prefilledEmailData?.prev_message,
            props.prefilledEmailData?.replyTo,
          ),
        thread_id: emailForm.thread_id,
        message_id: emailForm.message_id,
        is_reply_all: emailForm.is_reply_all,
        is_forward: emailForm.is_forward,
      });
    }
    let response;
    response = await DashboardService.sendEmail(payload);


    if (response.success) {
      if (!props.taskId) {
        message.success("Email sent successfully!");
      }
      emit("email-sent");
      internalDrawerVisible.value = false;
      eventBus.emit("activity-logged");
      resetFormState();
    } else {
      if (response.error === "Multiple to emails are not supported") {
        message.error(
          "Personalization is not supported when sending to multiple recipents, try adding to cc or bcc",
        );
      } else if (response.error) {
        showAlreadyExistsMessage({ customMessage: `Email could not be sent: ${response.error}` });
      } else {
        message.error("Email could not be sent! Please check your email account connection.");
      }
      internalDrawerVisible.value = false;
    }
  } catch (error) {
    console.error("Error sending email:", error);
    message.error(error.message || "An error occurred while sending the email");
  } finally {
    isSending.value = false;
  }
};

const toFetchSenderDetails = async () => {
  try {
    const personalMailboxesResponse = DashboardService.fetchPersonalMailboxes();
    const sharedMailboxesResponse = DashboardService.fetchSharedMailboxes();
    const [personalMailboxes, sharedMailboxes] = await Promise.all([
      personalMailboxesResponse,
      sharedMailboxesResponse,
    ]);

    let mailboxes = [];
    if (personalMailboxes && personalMailboxes.success) {
      mailboxes = mailboxes.concat(personalMailboxes.email_accounts || []);
    }

    if (sharedMailboxes && sharedMailboxes.success) {
      mailboxes = mailboxes.concat(sharedMailboxes.email_accounts || []);
    }

    senderEmails.value = mailboxes.filter(
      (account) => account.status !== "DISCONNECTED",
    );

    if (!props.isReply && !props.isForward && !props.prefilledEmailData?.from) {
      emailForm.from = senderEmails.value[0]?.email || "";
    } else {
      const selectedEmail = mailboxes.find((e) => e.email === emailForm.from);

      if (selectedEmail?.status === "DISCONNECTED") {
        emailForm.from = "";
      } else if (selectedEmail) {
        showAttachButton.value =
          selectedEmail.vendor === "GMAIL" ||
          selectedEmail.vendor === "OUTLOOK";
      } else {
        emailForm.from = "";
      }
    }
  } catch (error) {
    console.error("Error while fetching sender details: ", error);
    message.error("An error occurred while fetching the sender details");
    senderEmails.value = [];
  }
};

watch(
  () => emailForm.from,
  (newFromEmail) => {
    const selectedEmail = senderEmails.value?.find(
      (e) => e.email === newFromEmail,
    );
    if (selectedEmail) {
      showAttachButton.value =
        selectedEmail.vendor === "GMAIL" || selectedEmail.vendor === "OUTLOOK";
    }
  },
);

watch(
  () => senderEmails.value,
  (newSenderEmails) => {
    if (!newSenderEmails?.find((e) => e.email === emailForm.from)) {
      emailForm.from = newSenderEmails[0]?.email || "";
    }
  },
);

const toConnectEmailVendor = async (emailVendor) => {
  const integration = emailIntegrationOptions.value[emailVendor];
  const sourcePath = `${route.path}?${props.source === "navbar" ? "show_navbar_compose" : "show_compose"}=true`;
  await connectIntegration({ ...integration }, sourcePath);
};

const handleInput = (value) => {
  debouncedFetchContacts(value);
};

const { handleRecipientDrop } = useRecipientDrop(() => emailForm);

const extractEntityFromUrl = (url) => {
  if (!url) return null;
  const cleanUrl = url.replace(/^\//, '').split('?')[0];
  const entityPattern = /^(leads|contacts|companies)\/(\d+)$/i;
  const match = cleanUrl.match(entityPattern);
  
  if (match) {
    const [, entityType, entityId] = match;
    return {
      entity_type: entityType.toUpperCase(),
      entity_id: parseInt(entityId, 10)
    };
  }
  
  return null;
};

const toFetchContacts = async (search_query = "") => {
  try {
    const entityInfo = extractEntityFromUrl(route.path);
    
    let objectType = 'contacts';
    if (entityInfo?.entity_type === 'LEADS') {
      objectType = 'leads';
    }
    
    const response = await CRMService.searchAllFields({
      object_type: objectType,
      search_query: search_query,
      page: 1,
      page_size: 50,
    });
    
    if (response.success) {
      const contacts = (response.results || []).map((item) => transformCRMSearchResult(item));
      
      listOfContacts.value = contacts.filter(
        (item) => item?.email && item.email !== "null" && item.email !== "",
      );
    }
  } catch (error) {
    console.error("Error while fetching contacts:: ", error);
  }
};

const debouncedFetchContacts = debounce(toFetchContacts, 250);

const fetchIntegrations = async () => {
  try {
    const response = await DashboardService.getIntegrations();
    if (response?.success) {
      integrations.value = response?.integrations;
      const integrationsMap = {};
      integrations.value.forEach((integration) => {
        integrationsMap[integration.name] = integration;
      });
      emailIntegrationOptions.value = {
        ...integrationsMap,
      };
    }
  } catch (error) {
    console.error("Error fetching integration:", error);
    message.error("An error occurred while fetching integration");
  }
};

onMounted(() => {
  fetchSignature();
  fetchIntegrations();
  fetchTemplates();
  toFetchSenderDetails();
  initializeEmailForm();
});

watch(
  () => props.visible,
  (newValue) => {
    internalDrawerVisible.value = newValue;
  },
);

watch(
  internalDrawerVisible,
  (newQuery, oldQuery) => {
    if (newQuery) {
      nextTick(() => {
        if (emailForm?.to?.length) {
          subjectInputRef?.value?.focus();
        }
      });
    }
  },
  {
    immediate: true,
  },
);

const removeQueryParams = () => {
  router.replace({
    path: route.path,
    hash: route.hash,
    query: {},
  });
};

watch(
  () => internalDrawerVisible.value,
  async (newValue) => {
    if (newValue !== props.visible) {
      emit("update:visible", newValue);
    }
    if (newValue) {
      initializeEmailForm();
      await fetchTemplates();
      await toFetchSenderDetails();
      await toFetchContacts();
    } else {
      resetFormState();
      if (route.query.show_compose || route.query.show_navbar_compose) {
        removeQueryParams();
      }
    }
  },
);

watch(
  () => props.recipientTo,
  () => {
    updateRecipientTo();
  },
);

watch(
  () => props.prefilledEmailData,
  () => {
    if (props.prefilledEmailData) {
      initializeEmailForm();
    }
  },
);
</script>

<style>
.ant-modal-content {
  background-color: #121212 !important;
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.ant-modal-header {
  background-color: #121212 !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}

.ant-modal-title {
  color: white !important;
}

.ant-modal-body {
  background-color: #121212 !important;
}

.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #1d1d20;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #2d2d30;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #3d3d40;
}

.mail_reply_fields {
  width: 100%;
}
</style>
